import { router, usePage } from "@inertiajs/react";
import OpportunityDetailCard from "../../components/opportunities/opportunityDetailCard";
import Highlights from "../../components/opportunities/highlights";
import { FormattedMessage, useIntl } from "react-intl";
import Speakers from "../../components/opportunities/speakers";
import MapComponent from "../../components/common/mapComponent";
import {
    BanknotesIcon,
    ScaleIcon,
    ChevronRightIcon,
    XCircleIcon,
    ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import FilesModal from "../../components/opportunities/filesModal";
import RoundedContainerWithBadgedHeader from "../../components/common/containers/roundedContainerWithBadgedHeader";
import EligibilityChecker from "../../components/opportunities/eligibilityChecker";
import ReportOpportunity from "../../components/opportunities/reportOpportunitity";
import AppLayout from "../../layouts/appLayout";
import ShareUrl from "../../components/common/modals/ShareUrl";
import HostDetails from "../../components/opportunities/hostDetails";
import {
    Opportunity,
    OpportunityAdditionalAttributes,
    OpportunityAdditionalProperties,
    OpportunitySpeaker,
    OpportunitySpeakerAdditionalAttributes,
    OpportunityTypeString,
} from "@/types/opportunity";
import { Participant } from "@/types/participant";
import CompleteProfileModal from "@/components/common/modals/completeProfileModal";
import { useState } from "react";
import MobileApplyButton from "@/components/opportunities/mobileApplyButton";
import HostPersonDetails from "@/components/opportunities/hostPersonDetails";
import { generateOpportunityRewardString } from "@/utils/opportunities";

interface OpportunityDetailInterface {
    opportunity: Opportunity &
        Pick<
            OpportunityAdditionalProperties,
            | "collaborator"
            | "type"
            | "topics"
            | "speakers"
            | "location"
            | "rewards"
            | "resources"
            | "user"
            | "account"
        > &
        Pick<OpportunityAdditionalAttributes, "reward"> & {
            collaborator: {
                hasActiveLicense: boolean;
                initials: string;
                createdAt: string;
                approvedOpportunityCount: number;
                approvedGroupCount: number;
            };
            speakers: (OpportunitySpeaker &
                OpportunitySpeakerAdditionalAttributes)[];
            location: { fullAddress: string };
            publishedDiff: string;
            createdDiff: string;
            imageUrl: string;
            imageAlt?: string | null;
            logoUrl: string;
            closeAt: string;
            hasClosed: boolean;
            status: string;
            user: {
                avatarUrl: string;
                name: string;
                initials: string;
                approvedOpportunityCount: number;
                approvedGroupCount: number;
            };
            account: {
                logoUrl: string;
                initials: string;
                approvedOpportunityCount: string;
                approvedGroupCount: string;
            };
            resources: (OpportunityAdditionalProperties["resources"][0] & {
                publicUrl: string;
            })[];
            typeTag: OpportunityTypeString;
        };
    participant: Participant & { createdAt: string };
    content: string;
    mapsAPIKey: string;
    highlights: {
        component: string;
        legend: string;
        isHtml: boolean;
        body: string;
    }[];
    eligible: boolean;
    criteria: { label: string; eligible: boolean }[];
    title: string;
    image: string;
    description: string;
    genders: { legend: string }[];
    ethnicities: { legend: string }[];
    sexualities: { legend: string }[];
}

export default function OpportunityDetail({
    opportunity,
    participant,
    content,
    highlights,
    mapsAPIKey,
    eligible,
    criteria,
    title,
    image,
    description,
    genders,
    ethnicities,
    sexualities,
}: OpportunityDetailInterface) {
    const intl = useIntl();
    const [completeProfileOpen, setCompleteProfileOpen] = useState(false);
    const { auth } = usePage().props;
    const statuses = {
        event: {
            title: "You've registered",
            description: (
                <div className="pt-4 space-y-4">
                    <p className="text-sm">
                        <FormattedMessage id="You registered for this event" />{" "}
                        {participant?.createdAt}
                    </p>
                </div>
            ),
        },
        survey: {
            title: "You've taken part",
            description: (
                <div className="pt-4 space-y-4">
                    <p className="text-sm">
                        <FormattedMessage
                            id="You applied to complete this survey :timestamp. If you've not completed the survey, please refer to the confirmation email that we sent you."
                            values={{ timestamp: participant?.createdAt }}
                        />
                    </p>
                </div>
            ),
        },
        default: {
            title: "You've applied",
            description: (
                <div className="pt-4 space-y-4">
                    <p className="text-sm">
                        <FormattedMessage
                            id="You applied to take part in this opportunity :timestamp. Further details about the status of your application can be found in"
                            values={{ timestamp: participant?.createdAt }}
                        />{" "}
                        <a
                            href={route("my-voice")}
                            className="font-medium underline underline-offset-4"
                        >
                            <FormattedMessage id="My Voice" />
                        </a>
                    </p>
                </div>
            ),
        },
    };
    return (
        <AppLayout title={title} image={image} description={description}>
            <div>
                <div className="sm:max-w-[1600px] mx-auto sm:px-6 sm:pt-8 sm:pb-16">
                    <div className="hidden sm:block">
                        <h1 className="text-3xl font-semibold text-gray-900">
                            {opportunity.title}
                        </h1>
                        <div className="flex items-center mt-1 mb-4">
                            {opportunity.collaborator.hasActiveLicense && (
                                <>
                                    <a
                                        href={`/collaborators/${opportunity.collaborator.slug}`}
                                    >
                                        <h3 className="uppercase font-semibold text-red-600">
                                            {opportunity.collaborator.name}
                                        </h3>
                                    </a>

                                    <span className="text-gray-400 px-3">
                                        &bull;
                                    </span>
                                </>
                            )}
                            <span className="font-semibold text-gray-900">
                                <FormattedMessage id={opportunity.type.title} />
                            </span>
                            <span className="text-gray-400 px-3">&bull;</span>
                            <span className="font-light text-gray-500">
                                <FormattedMessage
                                    id="posted :date"
                                    values={{
                                        date:
                                            opportunity.publishedDiff ??
                                            opportunity.createdDiff,
                                    }}
                                />
                            </span>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-12 sm:gap-6">
                        <div className="sm:col-span-9">
                            <OpportunityDetailCard
                                application={false}
                                image_src={opportunity.imageUrl}
                                alt={opportunity.imageAlt ?? ""}
                                initials={opportunity?.collaborator?.initials}
                                logo={opportunity?.logoUrl}
                                logo_alt={opportunity?.collaborator?.name}
                                close_at={opportunity.close_at}
                                has_closed={opportunity.hasClosed}
                                collaborator={opportunity?.collaborator}
                            />
                            <div className="sm:hidden flex flex-col px-6 mt-6">
                                {opportunity?.collaborator
                                    ?.hasActiveLicense && (
                                    <a
                                        href={`/collaborators/${opportunity.collaborator.slug}`}
                                    >
                                        <h3 className="text-sm uppercase font-semibold text-red-600">
                                            {opportunity.collaborator.name}
                                        </h3>
                                    </a>
                                )}
                                <h1 className="text-2xl font-medium text-gray-900">
                                    {opportunity.title}
                                </h1>
                                <div className="flex items-center pb-4">
                                    <span className="font-medium text-gray-900">
                                        <FormattedMessage
                                            id={opportunity.type.title}
                                        />
                                    </span>
                                    <span className="text-gray-400 px-2">
                                        &bull;
                                    </span>
                                    <span className="font-light text-gray-500">
                                        <FormattedMessage
                                            id="posted :date"
                                            values={{
                                                date:
                                                    opportunity.publishedDiff ??
                                                    opportunity.createdDiff,
                                            }}
                                        />
                                    </span>
                                    <span className="text-gray-400 px-2">
                                        &bull;
                                    </span>
                                    <div className="sm:hidden">
                                        <ShareUrl
                                            url={window.location.href}
                                            postShare={(shareMethod) => {
                                                router.post(
                                                    route(
                                                        "share-opportunity",
                                                        opportunity.slug,
                                                    ),
                                                    { via: shareMethod },
                                                );
                                            }}
                                            trigger={(setOpen) => (
                                                <button
                                                    onClick={() =>
                                                        setOpen(true)
                                                    }
                                                    className="underline underline-offset-4 text-gray-500"
                                                >
                                                    <FormattedMessage id="Share" />
                                                </button>
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            <Highlights highlights={highlights} />
                            <div className="px-6 sm:px-0">
                                <div className="mt-6 pb-6 sm:mt-12 sm:pb-12 space-y-4 border-b border-b-gray-300">
                                    <h2 className="text-2xl font-medium text-gray-900">
                                        <FormattedMessage id="About" />
                                    </h2>
                                    <div
                                        className="prose lg:prose-lg max-w-none"
                                        dangerouslySetInnerHTML={{
                                            __html: content,
                                        }}
                                    />
                                </div>
                                <div className="mt-6 pb-6 sm:mt-12 sm:pb-12 space-y-4 border-b border-b-gray-300">
                                    <h2 className="text-2xl font-medium text-gray-900">
                                        <FormattedMessage id="Topics" />
                                    </h2>
                                    <div className="flex items-center space-x-4">
                                        {opportunity.topics.map((item) => (
                                            <span
                                                className="inline-flex items-center rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900"
                                                key={item.label}
                                            >
                                                <FormattedMessage
                                                    id={item.label}
                                                />
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                {opportunity.speakers.length > 0 && (
                                    <Speakers speakers={opportunity.speakers} />
                                )}
                                {opportunity.location && (
                                    <div className="mt-6 pb-6 sm:mt-12 sm:pb-12 space-y-4 border-b border-b-gray-300">
                                        <h2 className="text-2xl font-medium text-gray-900">
                                            <FormattedMessage id="Where to go" />
                                        </h2>
                                        {opportunity.location.latitude &&
                                            opportunity.location.longitude && (
                                                <MapComponent
                                                    lat={
                                                        opportunity.location
                                                            .latitude
                                                    }
                                                    long={
                                                        opportunity.location
                                                            .longitude
                                                    }
                                                    APIKey={mapsAPIKey}
                                                />
                                            )}
                                        <h4 className="text-base font-medium text-gray-900">
                                            {opportunity.location.fullAddress}
                                        </h4>
                                        {opportunity.location.notes && (
                                            <p className="font-light text-gray-900">
                                                {opportunity.location.notes}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {opportunity.rewards.length > 0 &&
                                    opportunity.reward && (
                                        <div className="mt-6 pb-6 sm:mt-12 sm:pb-12 space-y-4 border-b border-b-gray-300">
                                            <h2 className="text-2xl font-medium text-gray-900">
                                                <FormattedMessage id="What you'll receive" />
                                            </h2>
                                            <div className="flex items-start space-x-4">
                                                <div className="flex-none">
                                                    <div className="flex items-center justify-center size-16 border border-gray-300 rounded-md bg-white ">
                                                        <BanknotesIcon className="size-6" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold pt-2 pb-1">
                                                        <FormattedMessage id="Reward" />
                                                    </h4>
                                                    <p className="font-light leading-5">
                                                        {generateOpportunityRewardString(
                                                            opportunity,
                                                            intl,
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                <HostPersonDetails user={opportunity.user} />

                                {/* Account */}
                                {!opportunity.account.is_primary && (
                                    <HostDetails
                                        image={opportunity.account.logoUrl}
                                        name={opportunity.account.name}
                                        initials={opportunity.account.initials}
                                        createdAt={
                                            opportunity.account.created_at
                                        }
                                        approvedOpportunityCount={
                                            opportunity.account
                                                .approvedOpportunityCount
                                        }
                                        approvedGroupCount={
                                            opportunity.account
                                                .approvedGroupCount
                                        }
                                        description={
                                            opportunity.account.description
                                        }
                                    />
                                )}
                                {opportunity?.collaborator
                                    ?.hasActiveLicense && (
                                    <HostDetails
                                        link={route(
                                            "show-collaborator",
                                            opportunity.collaborator.slug,
                                        )}
                                        image={opportunity.logoUrl}
                                        name={opportunity.collaborator.name}
                                        initials={
                                            opportunity.collaborator.initials
                                        }
                                        createdAt={
                                            opportunity.collaborator.created_at!
                                        }
                                        approvedOpportunityCount={
                                            opportunity.collaborator
                                                .approvedOpportunityCount
                                        }
                                        approvedGroupCount={
                                            opportunity.collaborator
                                                .approvedGroupCount
                                        }
                                        description={
                                            opportunity.collaborator.description
                                        }
                                    />
                                )}
                                <div className="pt-12 grid grid-cols-1 sm:grid-cols-2 gap-5 pb-4">
                                    {opportunity?.collaborator && (
                                        <a
                                            href={
                                                opportunity.collaborator
                                                    .privacy_url
                                            }
                                            className="group"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <div className="bg-white group-hover:bg-gray-100 border border-gray-300 rounded-sm p-4 flex flex-col">
                                                <div className="flex flex-col justify-between">
                                                    <div className="pb-2">
                                                        <ScaleIcon className="size-6" />
                                                    </div>
                                                    <div className="flex justify-between items-center text-gray-900">
                                                        <span className="text-2xl font-medium">
                                                            <FormattedMessage id="Data & Privacy Policy" />
                                                        </span>
                                                        <span className="-mr-3">
                                                            <ChevronRightIcon
                                                                className="size-8"
                                                                strokeWidth="1"
                                                            />
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </a>
                                    )}
                                    {opportunity.resources.length > 0 && (
                                        <FilesModal
                                            files={opportunity.resources}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="sm:col-span-3">
                            {/* Right column */}
                            <div className="sm:sticky sm:top-4 hidden sm:block">
                                {opportunity.hasClosed ? (
                                    <RoundedContainerWithBadgedHeader
                                        Icon={XCircleIcon}
                                        title={
                                            opportunity.status === "cancelled"
                                                ? "Cancelled"
                                                : "Closed"
                                        }
                                    >
                                        <div className="pt-4 space-y-4">
                                            <p className="text-sm">
                                                <span className="font-semibold">
                                                    <FormattedMessage id="Sorry" />
                                                </span>{" "}
                                                {opportunity.status ===
                                                "cancelled" ? (
                                                    <FormattedMessage id="This opportunity has been cancelled." />
                                                ) : (
                                                    <FormattedMessage id="This opportunity is now closed." />
                                                )}
                                            </p>
                                        </div>
                                    </RoundedContainerWithBadgedHeader>
                                ) : participant ? (
                                    <RoundedContainerWithBadgedHeader
                                        Icon={ClipboardDocumentCheckIcon}
                                        title={
                                            // @ts-ignore
                                            statuses?.[opportunity.typeTag]
                                                ?.title ||
                                            statuses.default.title
                                        }
                                    >
                                        {/* @ts-ignore */}
                                        {statuses?.[opportunity.typeTag]
                                            ?.description ||
                                            statuses.default.description}
                                    </RoundedContainerWithBadgedHeader>
                                ) : (
                                    <EligibilityChecker
                                        opportunity={opportunity}
                                        criteria={criteria}
                                        eligible={eligible}
                                        setCompleteProfileOpen={
                                            setCompleteProfileOpen
                                        }
                                    />
                                )}
                                <div className="flex justify-center gap-x-4 items-baseline pt-2 pb-4 sm:pb-0">
                                    <ShareUrl
                                        postShare={(shareMethod) => {
                                            router.post(
                                                route(
                                                    "share-opportunity",
                                                    opportunity.slug,
                                                ),
                                                { via: shareMethod },
                                            );
                                        }}
                                        url={window.location.href}
                                        trigger={(setOpen) => (
                                            <button
                                                onClick={() => setOpen(true)}
                                                className="text-sm underline underline-offset-4 text-gray-700 hidden sm:block"
                                            >
                                                <FormattedMessage id="Share" />
                                            </button>
                                        )}
                                    />
                                    {auth.user && (
                                        <>
                                            <span className="text-gray-400 hidden sm:block">
                                                &bull;
                                            </span>
                                            <ReportOpportunity
                                                slug={opportunity.slug}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {auth?.user && (
                <CompleteProfileModal
                    open={completeProfileOpen}
                    setOpen={setCompleteProfileOpen}
                    genders={genders}
                    ethnicities={ethnicities}
                    sexualities={sexualities}
                    criteria={criteria}
                />
            )}
            <MobileApplyButton
                setCompleteProfileOpen={setCompleteProfileOpen}
                criteria={criteria}
                slug={opportunity.slug}
                closed={opportunity.hasClosed}
                participant={participant}
                type={opportunity.typeTag}
            />
        </AppLayout>
    );
}
